import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { getCategoryProfitability, getCategoryProfitabilityBreakDown, getCategoryProfitabilityBreakDownByCategory, getCategoryProfitabilityProductsSold } from '@/api/seller-analytics';
import {
    CategoryProfitAnalysisResponse,
    ErrorCode,
    ErrorSource,
    ProductsSold,
    ProfitabilityByCategoryBreakdown,
    ProfitabilityByCategoryOverviewResponseBody,
    ProfitabilityByCategoryProductsSold
} from "@boxem/core-api-schema";
import { DateRangeValue, endOfToday, startOfToday } from '@/utils/dateUtils';
import { getLocalStorageItem } from '@/utils/localStorageUtils';
import { useErrorModalContext } from './ErrorModalContext';

interface PaginatedData<T> {
    items: T[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

interface CategoryAnalyticsContextData {
    overviewData: ProfitabilityByCategoryOverviewResponseBody | null;
    profitabilityData: CategoryProfitAnalysisResponse | null;
    filteredAndPaginatedData: PaginatedData<CategoryProfitAnalysisResponse> | null;
    timeFrame: DateRangeValue;
    isOverviewLoading: boolean;
    isProfitabilityLoading: boolean;
    error: string | null;
    page: number;
    limit: number;
    search: string;
    totalPages: number;
    setTimeFrame: (time: DateRangeValue) => void;
    setPage: (page: number) => void;
    setLimit: (limit: number) => void;
    setSearch: (search: string) => void;
    fetchOverviewData: () => Promise<void>;
    fetchProfitabilityData: () => Promise<void>;
    setSort: (id: string, direction: 'asc' | 'desc') => void;
    individualCategoryProfitBreakDownData: ProfitabilityByCategoryBreakdown,
    isIndividualProfitabilityLoading: boolean,
    fetchIndividualProfitabilityData: (category: string) => Promise<void>,

    // productsSold
    productsSold: ProfitabilityByCategoryProductsSold | null;
    filteredAndPaginatedProductsSold: PaginatedData<ProductsSold> | null;
    isProductsSoldLoading: boolean;
    productsSoldSearch: string;
    productsSoldPage: number;
    productsSoldLimit: number;
    productsSoldTotalPages: number;
    productsSoldSortDirection: SortDirection;
    setProductsSoldSearch: (search: string) => void;
    setProductsSoldPage: (page: number) => void;
    setProductsSoldLimit: (limit: number) => void;
    setProductsSoldSort: (id: string, direction: 'asc' | 'desc') => void;
    fetchProductsSold: (category: string) => Promise<void>;
}

type SortDirection = 'asc' | 'desc' | null;

const CategoryAnalyticsContext = createContext<CategoryAnalyticsContextData | undefined>(undefined);

export const useCategoryAnalytics = () => {
    const context = useContext(CategoryAnalyticsContext);
    if (context === undefined) {
        throw new Error('useCategoryAnalytics must be used within a CategoryAnalyticsProvider');
    }
    return context;
};

export const CategoryAnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { setErrors } = useErrorModalContext();
    const [overviewData, setOverviewData] = useState<ProfitabilityByCategoryOverviewResponseBody | null>(null);
    const [profitabilityData, setProfitabilityData] = useState<CategoryProfitAnalysisResponse | null>(null);
    const [timeFrame, setTimeFrame] = useState<DateRangeValue>(
        getLocalStorageItem('analyticsTimeFrame', {
            from: startOfToday.startOf("month").startOf('day').toDate(),
            to: endOfToday.toDate(),
            label: 'This month'
        })
    );
    const [isOverviewLoading, setIsOverviewLoading] = useState<boolean>(false);
    const [isProfitabilityLoading, setIsProfitabilityLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(10);
    const [search, setSearch] = useState<string>('');
    const [sortedColumnId, setSortedColumnId] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [individualCategoryProfitBreakDownData, setIndividualCategoryProfitBreakDownData] = useState<ProfitabilityByCategoryBreakdown | null>(null);
    const [isIndividualProfitabilityLoading, setIsIndividualProfitabilityLoading] = useState<boolean>(false);

    const [productsSold, setProductsSold] = useState<ProfitabilityByCategoryProductsSold | null>(null);
    const [isProductsSoldLoading, setIsProductsSoldLoading] = useState<boolean>(false);
    const [productsSoldSearch, setProductsSoldSearch] = useState<string>('');
    const [productsSoldPage, setProductsSoldPage] = useState<number>(1);
    const [productsSoldLimit, setProductsSoldLimit] = useState<number>(5);
    const [productsSoldSortedColumnId, setProductsSoldSortedColumnId] = useState<string | null>(null);
    const [productsSoldSortDirection, setProductsSoldSortDirection] = useState<SortDirection>('asc');

    // Add sort handler for products sold
    const setProductsSoldSort = useCallback((id: string, direction: 'asc' | 'desc') => {
        setProductsSoldSortedColumnId(id);
        setProductsSoldSortDirection(direction);
    }, []);

    // Add search handler that resets pagination
    const setProductsSoldSearchLocal = useCallback((newSearch: string) => {
        setProductsSoldPage(1);
        setProductsSoldSearch(newSearch);
    }, []);

    // Add limit handler that resets pagination
    const setProductsSoldLimitLocal = useCallback((newLimit: number) => {
        setProductsSoldPage(1);
        setProductsSoldLimit(newLimit);
    }, []);

    // Add filtered and paginated data for products sold
    const filteredAndPaginatedProductsSold = useMemo<PaginatedData<ProductsSold>>(() => {
        if (!productsSold?.products) return null;

        let filtered = productsSold.products.filter(item => {
            const searchLower = productsSoldSearch.toLowerCase();
            return (
                item.name.toLowerCase().includes(searchLower) ||
                item.sku.toLowerCase().includes(searchLower) ||
                item.asin.toLowerCase().includes(searchLower)
            );
        });

        if (productsSoldSortedColumnId && productsSoldSortDirection) {
            filtered = [...filtered].sort((a, b) => {
                const aValue = a[productsSoldSortedColumnId];
                const bValue = b[productsSoldSortedColumnId];

                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return productsSoldSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return productsSoldSortDirection === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                return 0;
            });
        }

        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / productsSoldLimit);
        const startIndex = (productsSoldPage - 1) * productsSoldLimit;
        const endIndex = startIndex + productsSoldLimit;
        const paginatedItems = filtered.slice(startIndex, endIndex);

        return {
            items: paginatedItems,
            total: totalItems,
            page: productsSoldPage,
            limit: productsSoldLimit,
            totalPages
        };
    }, [productsSold, productsSoldSearch, productsSoldPage, productsSoldLimit, 
        productsSoldSortedColumnId, productsSoldSortDirection]);

    const productsSoldTotalPages = useMemo(() => {
        if (!filteredAndPaginatedProductsSold) return 0;
        return Math.ceil(filteredAndPaginatedProductsSold.total / productsSoldLimit);
    }, [filteredAndPaginatedProductsSold, productsSoldLimit]);

    const setSort = useCallback((id: string, direction: 'asc' | 'desc') => {
        setSortedColumnId(id);
        setSortDirection(direction);
    }, []);

    const fetchOverviewData = useCallback(async () => {
        setIsOverviewLoading(true);
        setError(null);
        try {
            const response = await getCategoryProfitabilityBreakDown({ interval: timeFrame });
            setOverviewData(response);
        } catch (err) {
            console.error('Error fetching category profitability overview:', err);
            setError('Failed to fetch profitability overview. Please try again.');
            setErrors([{ source: ErrorSource.BOXEM, message: 'Unable to load profitability data. Please try again', type: ErrorCode.GENERIC}]);
        } finally {
            setIsOverviewLoading(false);
        }
    }, [timeFrame]);

    const fetchProfitabilityData = useCallback(async () => {
        setIsProfitabilityLoading(true);
        setError(null);
        try {
            const response = await getCategoryProfitability({
                interval: timeFrame
            });
            setProfitabilityData(response);
        } catch (err) {
            console.error('Error fetching category profitability details:', err);
            setError('Failed to fetch profitability details. Please try again.');
            setErrors([{ source: ErrorSource.BOXEM, message: 'Unable to load profitability data. Please try again', type: ErrorCode.GENERIC}]);
        } finally {
            setIsProfitabilityLoading(false);
        }
    }, [timeFrame]);

    const fetchProductsSold = useCallback(async (category: string) => {
        setIsProductsSoldLoading(true);
        setError(null);
        try {
            const response = await getCategoryProfitabilityProductsSold({
                interval: timeFrame,
                category: category
            });
            setProductsSold(response);
        } catch (err) {
            console.error('Error fetching category profitability product sold:', err);
            setError('Failed to fetch category profitability product sold. Please try again.');
            setErrors([{ source: ErrorSource.BOXEM, message: 'Unable to load product sold during this time. Please try again', type: ErrorCode.GENERIC}]);
        } finally {
            setIsProductsSoldLoading(false);
        }
    }, [timeFrame]);

    const fetchIndividualProfitabilityData = useCallback(async (category: string) => {
        setIsIndividualProfitabilityLoading(true);
        setError(null);
        try {
          const response = await getCategoryProfitabilityBreakDownByCategory({
            interval: timeFrame,
            category: category
          });
          setIndividualCategoryProfitBreakDownData(response);
        } catch (err) {
          console.error('Error fetching individual category profitability details:', err);
          setError('Failed to fetch individual profitability details. Please try again.');
          setErrors([{ source: ErrorSource.BOXEM, message: 'Unable to load profitability data. Please try again', type: ErrorCode.GENERIC}]);
        } finally {
          setIsIndividualProfitabilityLoading(false);
        }
      }, [timeFrame]);

    const filteredAndPaginatedData = useMemo<PaginatedData<any>>(() => {
        if (!profitabilityData) return null;

        let filtered = profitabilityData.filter(item => {
            const searchLower = search.toLowerCase();
            return item.category.toLowerCase().includes(searchLower);
        });

        if (sortedColumnId && sortDirection) {
            filtered = [...filtered].sort((a, b) => {
                const aValue = a[sortedColumnId];
                const bValue = b[sortedColumnId];

                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return sortDirection === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                return 0;
            });
        }

        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / limit);
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedItems = filtered.slice(startIndex, endIndex);

        return {
            items: paginatedItems,
            total: totalItems,
            page,
            limit,
            totalPages
        };
    }, [profitabilityData, search, page, limit, sortedColumnId, sortDirection]);

    const totalPages = useMemo(() => {
        if (!filteredAndPaginatedData) return 0;
        return Math.ceil(filteredAndPaginatedData.total / limit);
    }, [filteredAndPaginatedData, limit]);

    const setSearchLocal = useCallback((newSearch: string) => {
        setPage(1);
        setSearch(newSearch);
    }, []);

    const setLimitLocal = useCallback((newLimit: number) => {
        setPage(1);
        setLimit(newLimit);
    }, []);

    return (
        <CategoryAnalyticsContext.Provider
            value={{
                overviewData,
                profitabilityData,
                filteredAndPaginatedData,
                timeFrame,
                isOverviewLoading,
                isProfitabilityLoading,
                error,
                page,
                limit,
                search,
                totalPages,
                setTimeFrame,
                setPage,
                setLimit: setLimitLocal,
                setSearch: setSearchLocal,
                fetchOverviewData,
                fetchProfitabilityData,
                setSort,
                individualCategoryProfitBreakDownData,
                isIndividualProfitabilityLoading,
                fetchIndividualProfitabilityData,
                
                //productsSold
                productsSold,
                filteredAndPaginatedProductsSold,
                isProductsSoldLoading,
                productsSoldSearch,
                productsSoldPage,
                productsSoldLimit,
                productsSoldTotalPages,
                productsSoldSortDirection,
                setProductsSoldSearch: setProductsSoldSearchLocal,
                setProductsSoldPage,
                setProductsSoldLimit: setProductsSoldLimitLocal,
                setProductsSoldSort,
                fetchProductsSold,
            }}
        >
            {children}
        </CategoryAnalyticsContext.Provider>
    );
};